import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { TextBox } from 'srcReact/components/common2/TextBox'
import Button from 'srcReact/components/common2/Button'
import Calendar from 'srcReact/components/common2/Calendar'
import ReactMenu from 'srcReact/components/common2/ReactMenu'
import Icon from 'srcReact/components/icons/Icon'
import { profileActions } from '../../../../../../action'
import styles from './styles.module.scss'
import Checkbox from 'srcReact/components/common2/Checkbox'
import Spinner from 'srcReact/components/common2/Spinner'
import ErrorMessage from 'srcReact/components/common2/ErrorMessage'
import Popup from 'reactjs-popup'
import { getCurrentOrgId } from 'srcReact/helpers/organizationHelper'
import { organizationActions } from 'srcReact/OrganizationPage'

export default function JourneyPopup({ isOpen, type, closeModal, ctaPopup = '' }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const {
    myProfile: { profileData, userExperience, userEducation },
    common: { isMobile, isNative }
  } = useSelector((state) => state)

  const [expId, setExpId] = useState('')
  const [role, setRole] = useState('')
  const [currentPosition, setCurrentPosition] = useState(false)
  const [company, setCompany] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [addPop, setAddPop] = useState(true)
  const [managePop, setManagePop] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [error, setError] = useState('')

  const isTypeEducation = () => {
    return type === 'education'
  }

  useEffect(() => {
    const { loading, updated, updateType, error } = userExperience
    if (!loading && updated) {
      if (updateType === 'delete' || updateType === 'update') {
        setManagePop(true)
        setAddPop(false)
      } else {
        closeModal()
      }
      callHeaderInfoForNative() //to update myprofile ctas
    }
    if (!loading && error) {
      handleServerError(error)
    }
  }, [userExperience])

  const handleServerError = (error) => {
    if (Array.isArray(error)) {
      setError(error.join(`\r\n`))
    } else {
      setError(error)
    }
  }

  const callHeaderInfoForNative = () => {
    if (isNative) {
      const organizationId = getCurrentOrgId()
      dispatch(organizationActions.getHeaderInfo(organizationId))
    }
  }

  useEffect(() => {
    const { loading, updated, updateType, error } = userEducation
    if (!loading && updated) {
      if (updateType === 'delete' || updateType === 'update') {
        setManagePop(true)
        setAddPop(false)
      } else {
        closeModal()
      }
    }
    if (!loading && error) {
      handleServerError(error)
    }
  }, [userEducation])

  useEffect(() => {
    return () => {
      setError('')
      dispatch(profileActions.resetExperiencePopup())
      dispatch(profileActions.resetEducationPopup())
    }
  }, [])

  const handleRole = (e) => {
    setError('')
    setRole({
      ...role,
      value: e.target.value
    })
  }

  const handleCompany = (e) => {
    setError('')
    setCompany({
      ...company,
      value: e.target.value
    })
  }

  const handleStartDate = (value) => {
    setError('')
    setStartDate({
      ...startDate,
      value: value
    })
  }

  const handleEndDate = (value) => {
    setError('')
    setEndDate({
      ...endDate,
      value: value
    })
  }

  const handleToggle = (type) => {
    if (type === 'add') {
      setAddPop(true)
      setManagePop(false)
      setEditMode(false)
      setRole({ ...role, value: '' })
      setCompany({ ...company, value: '' })
      setStartDate({ ...startDate, value: '' })
      setEndDate({ ...endDate, value: '' })
      setExpId({ ...expId, value: '' })
    } else if (type === 'manage') {
      if (userExperience.list.length > 0 || userEducation.list.length > 0) {
        setAddPop(false)
        setManagePop(true)
        setEditMode(false)
      }
    }
  }

  const handleConfirm = () => {
    if (!validate()) {
      return
    }
    const dates = {
      start_date: dayjs(startDate.value).format('YYYY-MM'),
      ...(endDate && endDate.value ? { end_date: dayjs(endDate.value).format('YYYY-MM') } : {})
    }
    if (editMode) {
      let payload = {}
      if (isTypeEducation()) {
        payload = {
          id: expId.value,
          institution: company.value,
          degree: role.value,
          ...dates
        }
        dispatch(profileActions.updateUserEducation(payload))
      } else {
        dispatch(
          profileActions.updateUserExperience(
            expId.value,
            company.value,
            role.value,
            dates.start_date,
            dates.end_date,
            ctaPopup
          )
        )
      }
    } else {
      let payload = {}

      if (isTypeEducation()) {
        payload = {
          institution: company.value,
          degree: role.value,
          ...dates
        }
        dispatch(profileActions.addUserEducation(payload))
      } else {
        dispatch(
          profileActions.addUserExperience(company.value, role.value, dates.start_date, dates.end_date, ctaPopup)
        )
      }
    }
  }

  const validate = () => {
    if (!company.value && !role.value) {
      if (isTypeEducation()) {
        setError(t('my_profile.popup.errorEducation'))
      } else {
        setError(t('my_profile.popup.errorExperience'))
      }
      return false
    }
    const endTimestamp = new Date(endDate.value).getTime()
    const startTimestamp = new Date(startDate.value).getTime()

    if (startTimestamp > endTimestamp) {
      setError(t('global.dateErrorCompare'))
      return false
    }
    return true
  }

  const handleEdit = (data) => {
    const startDateArr = data.start_date ? data.start_date.split('-') : []
    const endDateArr = data.end_date ? data.end_date.split('-') : []
    setAddPop(true)
    setManagePop(false)
    setEditMode(true)
    setExpId({ ...expId, value: data.id })
    setRole({ ...role, value: data.role || data.degree })
    setCompany({ ...company, value: data.organization || data.institution })
    setStartDate({ ...startDate, value: data.start_date ? new Date(startDateArr[0], startDateArr[1] - 1, 1) : '' })
    setCurrentPosition(data.end_date === null)
    setEndDate({ ...endDate, value: data.end_date ? new Date(endDateArr[0], endDateArr[1] - 1, 1) : '' })
  }

  const handleDelete = (id) => {
    const payload = {
      id: id.toString()
    }
    if (isTypeEducation()) {
      dispatch(profileActions.deleteUserEducation(payload))
    } else {
      dispatch(profileActions.deleteUserExperience(payload))
    }
  }

  const optionMenu = (data, id) => {
    const items = []
    items.push(
      {
        text: t('global.edit'),
        onClick: () => handleEdit(data)
      },
      {
        text: t('global.delete'),
        onClick: () => handleDelete(id)
      }
    )
    return items
  }
  const handleCheckboxChange = () => {
    setError('')
    setCurrentPosition(!currentPosition)
    setEndDate({ ...endDate, value: '' })
  }

  return (
    <Popup
      open={isOpen}
      onClose={closeModal}
      modal
      overlayStyle={{ background: 'rgba(54, 73, 88, 0.4)' }}
      contentStyle={{
        width: isMobile ? '95%' : 'fit-content',
        padding: '0px',
        borderRadius: '22px',
        boxShadow: '5px 2px 10px 0 rgba(14, 11, 40, 0.15)',
        background: 'white',
        border: 'none'
      }}>
      <div className={`${styles.experiencePopup}`}>
        <button className={styles.closeWrap} onClick={closeModal}>
          <Icon name='close' styleModifier={styles.close} />
        </button>
        <div className={`${styles.toggleLink}`}>
          <h2 className={`${styles.title}`}>
            {type === 'education'
              ? t('my_profile.info_block_edit.myEducation')
              : t('my_profile.info_block_edit.myJourney')}
          </h2>
          {isMobile && (userExperience.loading || userEducation.loading) && <Spinner />}
          <div className={`${styles.linkWrap}`}>
            <h2 className={`${styles.links} ${addPop ? styles.active : ''}`} onClick={() => handleToggle('add')}>
              {type === 'education' ? t('my_profile.popup.education_add') : t('my_profile.popup.experience_add')}
            </h2>
            {((isTypeEducation() && userEducation.list && userEducation.list.length > 0) ||
              (userExperience.list && userExperience.list.length > 0)) && (
              <h2
                className={`${styles.links} ${managePop ? styles.active : ''}`}
                onClick={() => handleToggle('manage')}>
                {type === 'education'
                  ? t('my_profile.popup.education_manage')
                  : t('my_profile.popup.experience_manage')}
              </h2>
            )}
            {!isMobile && (userExperience.loading || userEducation.loading) && <Spinner />}
          </div>
        </div>
        {addPop ? (
          <div className={`${styles.toggleForm}`}>
            <TextBox
              customWrapperCss={styles.inputWrap}
              customInputCss={styles.input}
              fieldInfo={role}
              onChange={handleRole}
              label={type === 'education' ? t('my_profile.popup.edu_label_1') : t('my_profile.popup.form_label_1')}
            />
            <TextBox
              customWrapperCss={styles.inputWrap}
              customInputCss={styles.input}
              fieldInfo={company}
              onChange={handleCompany}
              label={type === 'education' ? t('my_profile.popup.edu_label_2') : t('my_profile.popup.form_label_2')}
            />
            <div className={`${styles.dateWrap}`}>
              <h2 className={`${styles.dateTitle}`}>{t('global.from')}</h2>
              <div className={styles.fromDateWrapper}>
                <Calendar
                  defaultView='decade'
                  maxDetail='year'
                  defaultDate={new Date()}
                  date={startDate.value}
                  size={'medium'}
                  handleDateChange={handleStartDate}
                  containerCSS={styles.calendarContainer}
                  displayFormat='MMM-YYYY'
                  maxDate={new Date()}
                  calendarCSS={(isMobile || isNative) && styles.calendar}
                />
                <div className={styles.currentPosition}>
                  <Checkbox
                    customClassName={styles.currentPositionCheck}
                    checked={currentPosition}
                    name='currentPosition'
                    onChange={handleCheckboxChange}
                  />
                  <span className={styles.currentPositionText}>
                    {isTypeEducation() ? t('global.current') : t('my_profile.popup.currentPosition')}
                  </span>
                </div>
              </div>
            </div>
            <div className={`${styles.dateWrap}`}>
              <h2 className={`${styles.dateTitle}`}>{t('global.to')}</h2>
              <Calendar
                defaultView='decade'
                maxDetail='year'
                defaultDate={new Date()}
                date={endDate.value}
                // setRef={setStartDateRef}
                size={'medium'}
                handleDateChange={handleEndDate}
                containerCSS={styles.calendarContainer}
                displayFormat='MMM-YYYY'
                disabled={currentPosition}
                maxDate={new Date()}
                calendarCSS={(isMobile || isNative) && styles.calendar}
              />
            </div>
            {error && <ErrorMessage html={error} className={styles.error} />}
            <Button
              customStyle={styles.btnConfirm}
              text={t('global.confirm')}
              handleClick={handleConfirm}
              size='medium'
            />
          </div>
        ) : (
          ''
        )}
        {managePop ? (
          isTypeEducation() ? (
            <div className={`${styles.manageForm}`}>
              {userEducation.list &&
                userEducation.list.map((e, index) => (
                  <div key={index} className={`${styles.manageOuterWrap}`}>
                    <div className={`${styles.manageWrap}`}>
                      <h2 className={`${styles.role}`}>{e.degree}</h2>
                      <div className={`${styles.subWrap}`}>
                        <h2 className={`${styles.company}`}>{e.institution}</h2>
                        <h2 className={`${styles.date}`}>{`${dayjs(e.start_date).format('MMM YYYY')} - ${
                          e.end_date ? dayjs(e.end_date).format('MMM YYYY') : t('global.current')
                        }`}</h2>
                      </div>
                    </div>
                    <ReactMenu options={optionMenu(e, e.id)} />
                  </div>
                ))}
            </div>
          ) : (
            <div className={`${styles.manageForm}`}>
              {userExperience.list &&
                userExperience.list.map((e, index) => (
                  <div key={index} className={`${styles.manageOuterWrap}`}>
                    <div className={`${styles.manageWrap}`}>
                      <h2 className={`${styles.role}`}>{e.role}</h2>
                      <div className={`${styles.subWrap}`}>
                        <h2 className={`${styles.company}`}>{e.organization}</h2>
                        <h2 className={`${styles.date}`}>{`${dayjs(e.start_date).format('MMM YYYY')} - ${
                          e.end_date ? dayjs(e.end_date).format('MMM YYYY') : t('global.current')
                        }`}</h2>
                      </div>
                    </div>
                    <ReactMenu options={optionMenu(e, e.id)} />
                  </div>
                ))}
            </div>
          )
        ) : (
          ''
        )}
      </div>
    </Popup>
  )
}
